import { Suspense, useState, useEffect, useRef, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import { useNavigate, useLocation, useSearchParams, Outlet } from 'react-router-dom';
import { trackPageView, trackEvent, attachUserProps } from '@/analytics/analytics';
import { Menu, ChevronsLeft, LogOut, AlertCircle, Phone } from 'lucide-react';
import { Sun, Moon } from 'lucide-react';
import Sidebar from '@/components/Sidebar';
import CallConfirmationModal from '@/components/CallConfirmationModal';
import RealtimeVoiceAssistantOverlay from '@/components/RealtimeVoiceAssistantOverlay';
import CallSummaryModal from '@/components/CallSummaryModal';
import { useSubjectChapter } from '@/context/SubjectChapterContext';
import { fetchTutorCallInfo } from '@/api/chat';
import TeacherSidebar from '@/components/TeacherSidebar';
import MobileBottomNav from '@/components/MobileBottomNav';
import MobileTopBar from '@/components/MobileTopBar';
import TeacherHeader from '@/components/TeacherHeader';
import AuthPromptDialog from '@/components/AuthPromptDialog';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { ConfirmationModal } from '@/components/ui/ConfirmationModal';
import { cn } from '@/lib/utils';
import { TeacherProvider, useTeacher } from '@/context/TeacherContext';
import { getPortalType, clearAuthTokens, clearAllSessions, performLogoutTeardown } from '@/utils/auth';
import DailyLimitModal from '@/components/modals/DailyLimitModal';
import ResumeLearningModal from '@/components/modals/ResumeLearningModal';
import DetailedUserGuide from '@/components/DetailedUserGuide';
import { toast } from 'sonner';
import { usePreferences } from '@/context/PreferencesContext';
import { isAuthenticated as checkAuth, getUserData } from '@/utils/auth';
import { useNotifications } from '@/hooks/useNotifications';
import NotificationConsentModal from '@/components/Notifications/NotificationConsentModal';
import { hasAnonymousOnboarding } from '@/utils/anonymousAuth';
import AnonymousOnboardingModal from '@/components/AnonymousOnboardingModal';
import AIConsentModal from '@/components/AIConsentModal';
import { useUserProfile } from '@/context/UserProfileContext';
import { useCompliance } from '@/context/ComplianceContext';
import { preloadCoreStudentRoutes, preloadStudentRoute } from '@/routes/studentRoutePreloaders';
import { getVoiceLessonContext, subscribeToVoiceLessonContext } from '@/utils/voiceLessonContext';

const STUDENT_BOTTOM_NAV_ROUTE_KEY = 'budlee-student-bottom-nav-routes';

const STUDENT_ROUTE_DEFAULTS = {
  learn: '/student/learn',
  chat: '/student/chat',
  quests: '/student/quests?tab=new-quest',
  curiosity: '/student/curiosity',
  'curiosity-chapters': '/student/curiosity',
  'curiosity-topics': '/student/curiosity/topics',
  tuitions: '/student/tuitions',
  leaderboard: '/student/achievements/leaderboard',
  shop: '/student/achievements/shop',
  founders: '/student/achievements/founders',
  profile: '/student/profile',
  'affiliate-earnings': '/student/earnings',
  'students-analytics': '/student/analytics',
  settings: '/student/settings',
  about: '/student/about',
};

const getStudentRouteMemory = () => {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.sessionStorage.getItem(STUDENT_BOTTOM_NAV_ROUTE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const setStudentRouteMemory = (section, fullPath) => {
  if (typeof window === 'undefined' || !section || !fullPath) return;
  try {
    const next = getStudentRouteMemory();
    next[section] = fullPath;
    window.sessionStorage.setItem(STUDENT_BOTTOM_NAV_ROUTE_KEY, JSON.stringify(next));
  } catch {
    // best-effort navigation memory only
  }
};

const getRememberedStudentRoute = (section) => {
  const fallback = STUDENT_ROUTE_DEFAULTS[section] || '/student/learn';
  const remembered = getStudentRouteMemory()[section];
  if (typeof remembered !== 'string' || !remembered.startsWith('/student/')) {
    return fallback;
  }

  const rememberedPath = remembered.split('?')[0];
  const isValidForSection = (() => {
    switch (section) {
      case 'learn':
        return rememberedPath === '/student/learn';
      case 'chat':
        return rememberedPath === '/student/chat';
      case 'quests':
        return rememberedPath === '/student/quests' || rememberedPath.startsWith('/student/quests/');
      case 'curiosity':
      case 'curiosity-chapters':
        return (rememberedPath === '/student/curiosity' || rememberedPath.startsWith('/student/curiosity/chapter'))
          && !rememberedPath.startsWith('/student/curiosity/topics')
          && !rememberedPath.startsWith('/student/curiosity/advanced');
      case 'curiosity-topics':
        return rememberedPath === '/student/curiosity/topics' || rememberedPath.startsWith('/student/curiosity/topics/') || rememberedPath.startsWith('/student/curiosity/advanced');
      case 'tuitions':
        return rememberedPath === '/student/tuitions';
      case 'leaderboard':
        return rememberedPath === '/student/achievements/leaderboard';
      case 'shop':
        return rememberedPath === '/student/achievements/shop';
      case 'founders':
        return rememberedPath === '/student/achievements/founders';
      case 'profile':
        return rememberedPath === '/student/profile';
      case 'affiliate-earnings':
        return rememberedPath === '/student/earnings' || rememberedPath === '/student/affiliate-earnings';
      case 'students-analytics':
        return rememberedPath === '/student/analytics';
      case 'settings':
        return rememberedPath === '/student/settings';
      case 'about':
        return rememberedPath === '/student/about';
      default:
        return false;
    }
  })();

  return isValidForSection ? remembered : fallback;
};

const normalizeStudentRouteSection = (pathname = '') => {
  if (pathname.includes('/student/quests') || pathname.includes('/student/math-problems')) return 'quests';
  if (pathname.includes('/student/chat')) return 'chat';
  if (pathname.includes('/student/learn') && !pathname.includes('/curiosity')) return 'learn';
  if (pathname.includes('/student/curiosity/topics') || pathname.includes('/student/curiosity/advanced-video') || pathname.includes('/student/curiosity/advanced-article')) return 'curiosity-topics';
  if (pathname.includes('/student/curiosity')) return 'curiosity-chapters';
  if (pathname.includes('/student/tuitions')) return 'tuitions';
  if (pathname.includes('/student/achievements/leaderboard')) return 'leaderboard';
  if (pathname.includes('/student/achievements/shop')) return 'shop';
  if (pathname.includes('/student/achievements/founders')) return 'founders';
  if (pathname.includes('/student/analytics')) return 'students-analytics';
  if (pathname.includes('/student/profile')) return 'profile';
  if (pathname.includes('/student/earnings') || pathname.includes('/student/affiliate-earnings')) return 'affiliate-earnings';
  if (pathname.includes('/teacher/affiliate-earnings') || pathname.includes('/teacher/ambassador-earnings')) return 'affiliate-earnings';
  if (pathname.includes('/student/settings')) return 'settings';
  if (pathname.includes('/student/about')) return 'about';
  return null;
};

const StudentRouteLoadingFallback = () => (
  <div className="flex h-full min-h-0 flex-1 items-center justify-center bg-[hsl(var(--main-bg))] text-[hsl(var(--foreground))]">
    <div className="flex items-center gap-3 rounded-2xl border border-[hsl(var(--card-border))] bg-[hsl(var(--card-bg))]/90 px-5 py-4 shadow-lg">
      <span className="h-5 w-5 animate-spin rounded-full border-2 border-[hsl(var(--primary))] border-t-transparent" />
      <span className="text-sm font-semibold text-[hsl(var(--muted-foreground))]">Opening...</span>
    </div>
  </div>
);


export default function Layout({ activeSection = 'curiosity', portal: portalProp }) {
  const navigate = useNavigate();
  const location = useLocation();
  const portal = portalProp || getPortalType(location.pathname || '');
  const lastNavigationRef = useRef({ path: '', time: 0 });
  const { theme, toggleTheme, preferences, updatePreferences, config } = usePreferences();
  const isGuest = localStorage.getItem('is_guest_session') === 'true' || !checkAuth(portal);
  
  const { profile, refreshProfile } = useUserProfile();
  const { hide_tuition, hide_courses } = useCompliance();

  useEffect(() => {
    if (portal !== 'student') return;
    const pathname = location.pathname || '';
    if (hide_tuition && pathname.includes('/tuitions')) {
      navigate('/student/learn', { replace: true });
      return;
    }
    if (hide_courses && (pathname.includes('/student/curiosity/course-content') || (pathname.includes('/student/curiosity/topics') && location.search.includes('courseId=')))) {
      navigate('/student/curiosity/topics', { replace: true });
    }
  }, [portal, location.pathname, location.search, hide_tuition, hide_courses, navigate]);

  const { 
    unreadCount, 
    fetchUnreadCount, 
    confirmConsent, 
    cancelConsent,
    showConsentModal,
    setShowConsentModal
  } = useNotifications(true);

  useEffect(() => {
    if (showConsentModal) {
      console.log('[Layout] 🔔 Notification Soft Prompt is now VISIBLE');
    }
  }, [showConsentModal]);

  // ... existing code ...

  // Auto-detect activeSection from URL path for teacher portal
  const getActiveSectionFromPath = () => {
    if (portal === 'teacher') {
      const pathname = location.pathname || '';
      if (pathname.includes('/coaching-sessions')) return 'coaching-sessions';
      if (pathname.includes('/trainings')) return 'trainings';
      if (pathname.includes('/earnings')) return 'earnings';
      if (pathname.includes('/ai-tools')) return 'ai-tools';
      if (pathname.includes('/notes')) return 'notes';
      if (pathname.includes('/about')) return 'about';
      if (pathname.includes('/profile')) return 'profile';
      if (pathname.includes('/settings')) return 'settings';
      if (pathname.includes('/teacher/teach') || pathname.endsWith('/teach') || pathname === '/teacher' || pathname === '/teacher/') return 'teach';
      return 'teach';
    } else if (portal === 'student') {
      const pathname = location.pathname;
      if (pathname.includes('/tuitions')) return 'tuitions';
      if (pathname.includes('/quests')) return 'quests';
      if (pathname.includes('/chat')) return 'chat';
      if (pathname.includes('/math-problems')) return 'quests';
      if (pathname.includes('/learn') && !pathname.includes('/curiosity')) return 'learn';
      if (pathname.includes('/curiosity/topics') || pathname.includes('/curiosity/advanced-video') || pathname.includes('/curiosity/advanced-article')) return 'curiosity-topics';
      if (pathname.includes('/curiosity')) return 'curiosity-chapters';
      if (pathname.includes('/community')) return 'community';
      if (pathname.includes('/achievements/leaderboard')) return 'leaderboard';
      if (pathname.includes('/achievements/shop')) return 'shop';
      if (pathname.includes('/achievements/founders')) return 'founders';
      if (pathname.includes('/analytics')) return 'students-analytics';
      if (pathname.includes('/profile')) return 'profile';
      if (pathname.includes('/earnings') || pathname.includes('/affiliate-earnings')) return 'affiliate-earnings';
      if (pathname.includes('/settings')) return 'settings';
      if (pathname.includes('/about')) return 'about';
      // Default to learn for student portal
      return 'learn';
    }
    return activeSection;
  };

  const currentActiveSection = getActiveSectionFromPath();

  const teacherContext = useTeacher();
  const teacherTheme = teacherContext?.teacherTheme || 'light';
  const [isTeacherSidebarOpen, setIsTeacherSidebarOpen] = useState(false);

  useEffect(() => {
    if (portal !== 'student') return;
    const section = normalizeStudentRouteSection(location.pathname || '');
    if (!section) return;
    const fullPath = `${location.pathname}${location.search || ''}`;
    setStudentRouteMemory(section, fullPath);
  }, [portal, location.pathname, location.search]);

  useEffect(() => {
    if (portal !== 'student') return undefined;

    const schedulePreload = () => preloadCoreStudentRoutes();
    if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
      const idleId = window.requestIdleCallback(schedulePreload, { timeout: 1200 });
      return () => window.cancelIdleCallback?.(idleId);
    }

    const timeoutId = window.setTimeout(schedulePreload, 450);
    return () => window.clearTimeout(timeoutId);
  }, [portal]);

  const handleSectionChange = (section) => {
    preloadStudentRoute(section)?.catch(() => {});

    // Open upgrade plan modal if section is 'subscriptions' or 'upgrade-plan'
    if (section === 'subscriptions' || section === 'upgrade-plan') {
      setShowUpgradePlanModal(true);
      return;
    }

    // Prevent rapid navigation calls (throttling protection)
    const now = Date.now();
    let targetPath = '';

    // Determine target path based on section and portal
    switch (section) {
      case 'teach':
        targetPath = '/teacher/teach';
        break;
      case 'trainings':
        targetPath = '/teacher/trainings';
        break;
      case 'ai-tools':
        targetPath = '/teacher/ai-tools';
        break;
      case 'notes':
        targetPath = '/teacher/notes';
        break;
      case 'coaching-sessions':
        targetPath = '/teacher/coaching-sessions';
        break;
      case 'earnings':
        targetPath = '/teacher/earnings';
        break;
      case 'settings':
        if (portal === 'student') targetPath = getRememberedStudentRoute('settings');
        else if (portal === 'teacher') targetPath = '/teacher/settings';
        break;
      case 'availability':
        targetPath = '/teacher/settings';
        break;
      case 'learn':
        if (portal === 'student') targetPath = getRememberedStudentRoute('learn');
        else if (portal === 'teacher') targetPath = '/teacher/teach';
        break;
      case 'chat':
        targetPath = getRememberedStudentRoute('chat');
        break;
      case 'curiosity':
      case 'curiosity-chapters':
        targetPath = '/student/curiosity';
        break;
      case 'curiosity-topics':
        targetPath = '/student/curiosity/topics';
        break;
      case 'quests':
        targetPath = getRememberedStudentRoute('quests');
        break;
      case 'tuitions':
        targetPath = getRememberedStudentRoute('tuitions');
        break;
      case 'leaderboard':
        targetPath = getRememberedStudentRoute('leaderboard');
        break;
      case 'shop':
        targetPath = getRememberedStudentRoute('shop');
        break;
      case 'founders':
        targetPath = getRememberedStudentRoute('founders');
        break;
      case 'profile':
        if (portal === 'student') targetPath = getRememberedStudentRoute('profile');
        else if (portal === 'teacher') targetPath = '/teacher/profile';
        break;
      case 'students-analytics':
        if (portal === 'student') targetPath = getRememberedStudentRoute('students-analytics');
        break;
      case 'more':
        targetPath = getRememberedStudentRoute('learn');
        break;
      case 'affiliate-earnings':
        if (portal === 'student') targetPath = '/student/earnings';
        else if (portal === 'teacher') targetPath = '/teacher/affiliate-earnings';
        break;
      case 'subscriptions':
        targetPath = ''; // Handle modal trigger, no navigation
        break;
      case 'upgrade-plan':
        targetPath = ''; // Handle modal trigger, no navigation
        break;
      case 'about':
        if (portal === 'student') targetPath = getRememberedStudentRoute('about');
        else if (portal === 'teacher') targetPath = '/teacher/about';
        break;
      case 'new-quest':
      case 'ongoing':
      case 'completed':
      case 'drafts':
        if (portal === 'student') targetPath = `/student/quests?tab=${section}`;
        break;
      case 'quick-guide':
        targetPath = ''; // Modal trigger, no navigation
        break;

      default:
        if (portal === 'student') targetPath = '/student/learn';
        else if (portal === 'teacher') targetPath = '/teacher/teach';
    }

    // Prevent navigation if:
    // 1. Already on the target path
    // 2. Same navigation was called within last 300ms
    const currentFullPath = `${location.pathname}${location.search || ''}`;
    if (targetPath && targetPath !== currentFullPath) {
      const lastNav = lastNavigationRef.current;
      if (lastNav.path === targetPath && (now - lastNav.time) < 300) {
        return; // Skip rapid duplicate navigation
      }

      lastNavigationRef.current = { path: targetPath, time: now };

      if (portal === 'student' && location.pathname.startsWith('/student/learn') && !targetPath.startsWith('/student/learn')) {
        try {
          window.sessionStorage.setItem('learn_chat_open', 'false');
          window.dispatchEvent(new CustomEvent('budleeContentShellMode', {
            detail: { iconOnly: false },
          }));
        } catch {
          // best effort only
        }
      }
      
      // Check for active quest blockade only while the user is inside an active quest attempt.
      const currentPath = location.pathname || '';
      const isQuestAttemptRoute = /^\/student\/quests\/[^/]+\/(?:practice|test)(?:\/)?$/.test(currentPath);
      if (isQuestAttemptRoute && window.__QUEST_ACTIVE__) {
        setPendingNavPath(targetPath);
        setShowNavBlockerModal(true);
        return;
      }

      // Defer navigation slightly to prevent React 18 state batching from swallowing the route change
      // when synchronous events (like budleeContentShellMode) are dispatched.
      setTimeout(() => {
        navigate(targetPath);
      }, 0);
    }
  };

  const handleLogout = () => {
    setShowLogoutModal(true);
    setIsSidebarOpen(false); // Close sidebar when logout modal opens
  };

  const handleLogoutConfirmed = async () => {
    try {
      // De-register push token before logout (best effort)
      const fcmToken = localStorage.getItem('fcm_token');
      if (fcmToken) {
        try {
          const { deregisterPushToken } = await import('@/api/notifications');
          await deregisterPushToken(fcmToken);
        } catch (err) {
          console.warn('Failed to de-register push token:', err);
        }
      }
    } catch (error) {
      console.error('Error during logout:', error);
    } finally {
      setShowLogoutModal(false);
      await performLogoutTeardown();
    }
  };

  const [studentNavIconOnly, setStudentNavIconOnly] = useState(false);

  useEffect(() => {
    const handleBudleeShellMode = (event) => {
      setStudentNavIconOnly(Boolean(event.detail?.iconOnly));
    };
    window.addEventListener('budleeContentShellMode', handleBudleeShellMode);
    return () => window.removeEventListener('budleeContentShellMode', handleBudleeShellMode);
  }, []);

  useEffect(() => {
    if (portal !== 'student' || !location.pathname.startsWith('/student/learn')) {
      setStudentNavIconOnly(false);
    }
  }, [portal, location.pathname]);

  const shouldUseIconOnlyStudentNav = portal === 'student' && studentNavIconOnly;

  // Render appropriate sidebar based on portal
  const renderSidebar = (onNavigate = () => { }, showCloseButton = false, onClose = () => { }, onLogout = () => { }) => {
    if (portal === 'teacher') {
      return <TeacherSidebar activeSection={currentActiveSection} setActiveSection={handleSectionChange} onNavigate={onNavigate} showCloseButton={showCloseButton} onClose={onClose} onLogout={onLogout} />;
    } else {
      return (
        <Sidebar
          activeSection={currentActiveSection}
          setActiveSection={handleSectionChange}
          onNavigate={onNavigate}
          showCloseButton={showCloseButton}
          onClose={onClose}
          onLogout={onLogout}
          onUnlockFullAccess={() => navigate('/signup')}
          onCallClick={handleCallButtonClick}
          isCallDisabled={!isMicAllowed}
          iconOnly={shouldUseIconOnlyStudentNav && !showCloseButton}
        />
      );
    }
  };

  // Render Student Portal normally
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showNavBlockerModal, setShowNavBlockerModal] = useState(false);
  const [showUpgradePlanModal, setShowUpgradePlanModal] = useState(false);

  // Automatically trigger upgrade plan modal if URL has upgrade=true or subscribe=true
  useEffect(() => {
    if (location.search && (location.search.includes('upgrade=true') || location.search.includes('subscribe=true'))) {
      setShowUpgradePlanModal(true);
    }
    try {
      const searchParams = new URLSearchParams(location.search || window.location.search || '');
      const couponParam = searchParams.get('coupon');
      if (couponParam && couponParam.trim()) {
        localStorage.setItem('active_coupon_code', couponParam.trim().toUpperCase());
      }
    } catch (_) {}
  }, [location.search]);
  const [globalAuthPromptState, setGlobalAuthPromptState] = useState({ show: false, errorType: 402, isFreeUser: false });
  const [dailyLimitModalState, setDailyLimitModalState] = useState({
    show: false,
    feature: '',
    usedCount: 0,
    maxCount: 0,
    statusCode: 402
  });
  const [showDetailedUserGuide, setShowDetailedUserGuide] = useState(false);
  const [showCallConfirm, setShowCallConfirm] = useState(false);
  // On native Capacitor apps, microphone permission is managed at the OS level.
  // The Web Permissions API & getUserMedia are unreliable indicators on native WebViews,
  // so we always optimistically enable the call button and let the native stack handle it.
  const isNativeApp = typeof Capacitor !== 'undefined' && Capacitor.isNativePlatform();
  const [isMicAllowed, setIsMicAllowed] = useState(true);
  const [showCallOverlay, setShowCallOverlay] = useState(false);
  const [activeTutor, setActiveTutor] = useState(null);
  const [voiceLessonContext, setVoiceLessonContext] = useState(() => getVoiceLessonContext());
  const [showAIConsent, setShowAIConsent] = useState(false);
  const [showCallSummary, setShowCallSummary] = useState(false);
  const [callSummaryData, setCallSummaryData] = useState(null);

  useEffect(() => subscribeToVoiceLessonContext(setVoiceLessonContext), []);

  const checkMicrophonePermission = useCallback(async () => {
    // On Capacitor native apps, the Web Permissions API is unreliable.
    // OS-level permission is already handled by Capacitor; always allow.
    if (isNativeApp) {
      setIsMicAllowed(true);
      return;
    }

    try {
      if (navigator.permissions && navigator.permissions.query) {
        const result = await navigator.permissions.query({ name: 'microphone' });
        setIsMicAllowed(result.state !== 'denied');
        
        // Listen for changes
        result.onchange = () => {
          setIsMicAllowed(result.state !== 'denied');
        };
        return;
      }
    } catch (e) {
      // Not supported
    }

    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const hasAudioInput = devices.some(d => d.kind === 'audioinput');
      setIsMicAllowed(hasAudioInput);
    } catch (e) {
      setIsMicAllowed(false);
    }
  }, [isNativeApp]);

  useEffect(() => {
    checkMicrophonePermission();
    if (!isNativeApp) {
      window.addEventListener('focus', checkMicrophonePermission);
      return () => {
        window.removeEventListener('focus', checkMicrophonePermission);
      };
    }
  }, [checkMicrophonePermission, isNativeApp]);

  const { selectedSubject, selectedSubjectId, selectedChapter, selectedChapterId, subjects } = useSubjectChapter();

  const handleCallButtonClick = async () => {
    if (isGuest) {
      setGlobalAuthPromptState({
        show: true,
        feature: 'voice_tutor',
        errorType: 'voice_tutor',
        isFreeUser: false,
      });
      return;
    }

    // On Capacitor native apps, skip Web API mic checks entirely.
    // The native OS permission is already granted at this point.
    if (!isNativeApp) {
      let alreadyGranted = false;
      try {
        if (navigator.permissions && navigator.permissions.query) {
          const result = await navigator.permissions.query({ name: 'microphone' });
          alreadyGranted = result.state === 'granted';
        }
      } catch (e) {
        alreadyGranted = false;
      }

      if (!alreadyGranted) {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
          stream.getTracks().forEach(track => track.stop());
          setIsMicAllowed(true);
        } catch (err) {
          setIsMicAllowed(false);
          toast.error('Microphone permission required', {
            description: 'Please enable microphone access in your browser or device settings to use voice calls.',
          });
          return;
        }
      }
    }

    const hasConsent = preferences?.ai_data_consent || localStorage.getItem('budlee_ai_consent') === 'true';
    if (!hasConsent) {
      setShowAIConsent(true);
    } else {
      setShowCallConfirm(true);
    }
  };

  const handleAcceptAIConsent = async () => {
    localStorage.setItem('budlee_ai_consent', 'true');
    if (updatePreferences) {
      try {
        await updatePreferences({ ai_data_consent: true });
      } catch (err) {
        console.error('Failed to update AI data consent preference:', err);
      }
    }
    setShowAIConsent(false);
    setShowCallConfirm(true);
  };

  const handleDeclineAIConsent = () => {
    setShowAIConsent(false);
  };

  const shouldSkipDetailedUserGuide = (() => {
    const userData = getUserData(portal);
    const userType = (userData?.user_type || userData?.userType || '').toLowerCase();
    const isB2B = location.pathname.startsWith('/b2b') || userType === 'b2b' || userType === 'institute_admin';
    return userType === 'teacher' || userType === 'guardian' || portal === 'teacher' || location.pathname.startsWith('/teacher') || isB2B;
  })();

  // Global listener for paymentRequired events (429/402 from axios interceptor)
  // This ensures the subscription modal is shown even if the page component hasn't mounted yet
  useEffect(() => {
    const handleGlobalPaymentRequired = (event) => {
      const errorType = event.detail?.statusCode || 402;
      const isFreeUser = event.detail?.isFreeUser || false;
      const feature = event.detail?.feature || 'this feature';

      // If user is authenticated (not guest), show DailyLimitModal instead
      const isActuallyGuest = localStorage.getItem('is_guest_session') === 'true';
      const hasToken = !!(
        localStorage.getItem('student_token') ||
        localStorage.getItem('teacher_token') ||
        localStorage.getItem('access_token')
      );
      const isAuthenticated = hasToken && !isActuallyGuest;
      const limitScope = isAuthenticated ? 'free' : 'guest';
      const maxCount = event.detail?.max_count || 0;
      const remaining = event.detail?.remaining || 0;
      const usedCount = Math.max(0, maxCount - remaining);

      try {
        trackEvent('usage_limit_reached', attachUserProps({
          page: location.pathname,
          feature,
          status_code: errorType,
          limit_scope: limitScope,
          is_free_user: isAuthenticated,
          max_count: maxCount,
          remaining,
          used_count: usedCount,
        }));
      } catch {}

      if (isAuthenticated && (errorType === 402 || errorType === 429)) {
        setDailyLimitModalState({
          show: true,
          feature: feature,
          usedCount,
          maxCount,
          statusCode: errorType,
          directParentUnlock: event.detail?.directParentUnlock || false
        });
      } else {
        setGlobalAuthPromptState({ show: true, errorType, isFreeUser, feature });
      }
    };

    const handleRateLimitWarning = (event) => {
      const { feature } = event.detail;
      const FeatureNameMap = {
        'quest': 'Quest Generation',
        'quests': 'Quest Generation',
        'chat': 'AI Doubts',
        'learn': 'Learning pack content',
        'budlee_content': 'Budlee Content',
        'budlee content': 'Budlee Content',
        'curiosity': 'Curiosity content',
        'upload': 'Notes upload',
        'advanced_topics': 'Advanced Topics',
      };
      const displayName = FeatureNameMap[feature.toLowerCase()] || feature;
      toast.info(`80% of your ${displayName} limit for the day is reached. Upgrade for more access.`);
    };

    const handleShowDetailedGuide = (event) => {
      if (shouldSkipDetailedUserGuide) return;
      setShowDetailedUserGuide(true);
    };

    const handleTriggerAICall = () => {
      handleCallButtonClick();
    };

    window.addEventListener('paymentRequired', handleGlobalPaymentRequired);
    window.addEventListener('rateLimitWarning', handleRateLimitWarning);
    window.addEventListener('showDetailedUserGuide', handleShowDetailedGuide);
    window.addEventListener('triggerAICall', handleTriggerAICall);
    return () => {
      window.removeEventListener('paymentRequired', handleGlobalPaymentRequired);
      window.removeEventListener('rateLimitWarning', handleRateLimitWarning);
      window.removeEventListener('showDetailedUserGuide', handleShowDetailedGuide);
      window.removeEventListener('triggerAICall', handleTriggerAICall);
    };
  }, []);

  // Page timing for curiosity pages
  const pageStartRef = useRef(null);

  useEffect(() => {
    // Track pageview on route change
    try {
      trackPageView(location.pathname);
    } catch (e) { }

    // Page time tracking: start timer when entering curiosity (student)
    // and for teacher portals; send when leaving those areas
    try {
      const pathname = location.pathname || '';
      const isStudentCuriosity = pathname.startsWith('/student/curiosity');
      const isTeacher = portal === 'teacher';
      const shouldTrack = isStudentCuriosity || isTeacher;

      if (shouldTrack) {
        // start timer if not already started
        if (!pageStartRef.current) pageStartRef.current = Date.now();
      } else {
        // leaving tracked area
        if (pageStartRef.current) {
          const timeSpent = Math.round((Date.now() - pageStartRef.current) / 1000);
          trackEvent('page_time_spent', attachUserProps({ page: pathname, time_spent_seconds: timeSpent }));
          pageStartRef.current = null;
        }
      }
    } catch (e) { }
  }, [location.pathname, portal]);

  // Render Teacher Portal with Teacher theme handling
  if (portal === 'teacher') {
    const isDark = teacherTheme === 'dark';
    return (
      <div
        data-app-shell-root
        className={cn("fixed inset-0 h-[100dvh] flex overflow-hidden overscroll-none", isDark ? "bg-[hsl(var(--main-bg))]" : "bg-[#FAF8F5]")}
      >
        <div className="hidden lg:block">
          <TeacherSidebar activeSection={currentActiveSection} setActiveSection={handleSectionChange} onLogout={handleLogout} />
        </div>

        {/* Floating Sidebar Overlay when opened (only for small screens) */}
        {isTeacherSidebarOpen && (
          <div data-app-shell-sidebar-overlay className="fixed inset-0 z-[9999] flex lg:hidden bg-black/60 backdrop-blur-sm animate-fade-in">
            <div className="w-4/5 max-w-sm h-full shadow-2xl relative z-50 animate-in slide-in-from-left duration-300">
              <TeacherSidebar
                activeSection={currentActiveSection}
                setActiveSection={handleSectionChange}
                className="w-full h-full border-r shadow-2xl"
                onNavigate={() => setIsTeacherSidebarOpen(false)}
                onClose={() => setIsTeacherSidebarOpen(false)}
                showCloseButton={false}
                onLogout={handleLogout}
              />
            </div>
            <div className="flex-1 bg-transparent" onClick={() => setIsTeacherSidebarOpen(false)} />
          </div>
        )}
        <div className="flex-1 flex flex-col overflow-hidden min-w-0 min-h-0">
          <TeacherHeader onOpenSidebar={() => setIsTeacherSidebarOpen(true)} />
          <div className="flex-1 overflow-auto min-h-0 pb-safe pb-6 pl-safe-left pr-safe-right">
            <Outlet />
          </div>
        </div>

        {/* Logout Confirmation Modal for Teacher */}
        <ConfirmationModal
          isOpen={showLogoutModal}
          onOpenChange={setShowLogoutModal}
          title="Log out"
          description="You will be signed out of the teacher portal and returned to login."
          confirmText="Log out"
          cancelText="Stay logged in"
          onConfirm={handleLogoutConfirmed}
          onCancel={() => setShowLogoutModal(false)}
          icon={LogOut}
          zIndex="z-[10000]"
        />

        {/* Notification Soft Prompt Modal for Teacher */}
        <NotificationConsentModal
          isOpen={showConsentModal}
          onOpenChange={setShowConsentModal}
          onConfirm={confirmConsent}
          onCancel={cancelConsent}
          portal="teacher"
        />
      </div>
    );
  }

  const needsOnboarding = isGuest && !hasAnonymousOnboarding();
  const isBlockingModalOpen = 
    (globalAuthPromptState.show && globalAuthPromptState.errorType === 429) || 
    (dailyLimitModalState.show && dailyLimitModalState.statusCode === 429);

  return (
    <div
      data-app-shell-root
      className={cn(
        'fixed inset-0 h-[100dvh] flex bg-[hsl(var(--main-bg))] overflow-hidden overscroll-none',
        theme === 'dark' && 'dark',
      )}
    >
      {needsOnboarding && <AnonymousOnboardingModal onComplete={() => window.location.reload()} />}
      {/* Persistent Sidebar on large screens */}
      <div
        data-app-shell-sidebar
        className={cn("hidden lg:block", isBlockingModalOpen && "pointer-events-none opacity-50")}
      >
        {renderSidebar(() => { }, false, () => { }, handleLogout)}
      </div>

      {/* Floating Sidebar Overlay when opened (only for small screens) */}
      {isSidebarOpen && (
        <div data-app-shell-sidebar-overlay className="fixed inset-0 z-[9999] flex lg:hidden">
          <div className="w-4/5 max-w-sm h-full shadow-lg relative z-50">
            {renderSidebar(() => setIsSidebarOpen(false), true, () => setIsSidebarOpen(false), handleLogout)}
          </div>
          <div className="flex-1 bg-transparent" onClick={() => setIsSidebarOpen(false)} />
        </div>
      )}

      <div data-app-shell-main className="flex-1 flex flex-col min-w-0 min-h-0 overflow-hidden relative">
        {/* Mobile Top App Bar for Student Portal */}
        {/* Spacer for Mobile Top App Bar (since it is now fixed) */}
        {portal === 'student' && (
          <div
            data-app-shell-mobile-top-spacer
            className="lg:hidden h-[calc(3.5rem+max(env(safe-area-inset-top,0px),var(--safe-area-inset-top,0px)))] w-full flex-shrink-0"
          />
        )}

        {/* Main Content Area (disable overlay/blur on large screens) */}
        <div
          data-app-shell-content
          className={cn(
            'flex-1 flex flex-col overflow-hidden min-h-0 transition-all duration-300',
            (isSidebarOpen || dailyLimitModalState.show) ? 'lg:filter blur-xl lg:opacity-30 lg:pointer-events-none' : '',
            dailyLimitModalState.show ? 'filter blur-md opacity-70 pointer-events-none' : ''
          )}
        >
          <Suspense fallback={<StudentRouteLoadingFallback />}>
            <div key={location.pathname} className="flex h-full min-h-0 flex-1 flex-col overflow-hidden">
              <Outlet />
            </div>
          </Suspense>
        </div>

        {/* Mobile Bottom Navigation for Student Portal */}
        {/* Spacer for Mobile Bottom Navigation (since it is now fixed) */}
        {portal === 'student' && (
          <div
            data-app-shell-mobile-bottom-spacer
            className={cn(
              'lg:hidden w-full flex-shrink-0 transition-all duration-300',
              shouldUseIconOnlyStudentNav
                ? 'h-[calc(3.25rem+env(safe-area-inset-bottom,0px))]'
                : 'h-[calc(4rem+env(safe-area-inset-bottom,0px))]'
            )}
          />
        )}
      </div>

      {/* Logout Confirmation Modal */}
      <ConfirmationModal
        isOpen={showLogoutModal}
        onOpenChange={setShowLogoutModal}
        title="Log out"
        description={`You will be signed out of the ${portal} portal and returned to login.`}
        confirmText="Log out"
        cancelText="Stay logged in"
        onConfirm={handleLogoutConfirmed}
        onCancel={() => setShowLogoutModal(false)}
        icon={LogOut}
        zIndex="z-[10000]"
      />

      {/* Upgrade Plan Modal (from sidebar) */}
      <AuthPromptDialog
        isOpen={showUpgradePlanModal}
        onClose={() => setShowUpgradePlanModal(false)}
        mode="upgrade"
        showCloseButton={false}
      />

      {/* Global Rate Limit / Payment Required Modal */}
      {portal === 'student' && (
        <>
          <ResumeLearningModal />
          <AuthPromptDialog
            isOpen={globalAuthPromptState.show}
            onClose={() => setGlobalAuthPromptState(prev => ({ ...prev, show: false }))}
            mode={globalAuthPromptState.isFreeUser ? 'upgrade' : 'signup'}
            feature={globalAuthPromptState.feature}
            errorType={globalAuthPromptState.errorType}
            isFreeUser={globalAuthPromptState.isFreeUser}
          />

          <DailyLimitModal
            isOpen={dailyLimitModalState.show}
            onClose={() => setDailyLimitModalState(prev => ({ ...prev, show: false, directParentUnlock: false }))}
            onSeePlans={() => {
              setDailyLimitModalState(prev => ({ ...prev, show: false, directParentUnlock: false }));
              setGlobalAuthPromptState({
                show: true,
                errorType: dailyLimitModalState.statusCode,
                isFreeUser: true,
                feature: dailyLimitModalState.feature
              });
            }}
            feature={dailyLimitModalState.feature}
            usedCount={dailyLimitModalState.usedCount}
            maxCount={dailyLimitModalState.maxCount}
            statusCode={dailyLimitModalState.statusCode}
            directParentUnlock={dailyLimitModalState.directParentUnlock}
          />

        </>
      )}

      {/* Detailed User Guide Modal */}
      {!shouldSkipDetailedUserGuide && (
        <DetailedUserGuide 
          isOpen={showDetailedUserGuide} 
          onClose={() => setShowDetailedUserGuide(false)} 
        />
      )}
      {/* Functional Fixed Bars (sit on top level body layer) */}
      {portal === 'student' && (
        <>
          <MobileTopBar onOpenSidebar={() => setIsSidebarOpen(true)} onCallClick={handleCallButtonClick} isCallDisabled={!isMicAllowed} isDisabled={showCallOverlay} />
          <MobileBottomNav
            activeSection={currentActiveSection}
            onSectionChange={handleSectionChange}
            onNavigate={() => setIsSidebarOpen(false)}
            onLogout={handleLogout}
            onUnlockFullAccess={() => navigate('/signup')}
            isDisabled={showCallOverlay}
            iconOnly={shouldUseIconOnlyStudentNav}
          />
        </>
      )}
      {/* Navigation Block Confirmation Modal */}
      <ConfirmationModal
        isOpen={showNavBlockerModal}
        onOpenChange={setShowNavBlockerModal}
        title="Leave Quest?"
        description="Are you sure you want to exit? Your progress for the current question might not be saved."
        confirmText="Leave Session"
        cancelText="Cancel"
        onConfirm={() => {
          if (pendingNavPath) {
            navigate(pendingNavPath);
          }
          setShowNavBlockerModal(false);
          setPendingNavPath(null);
        }}
        onCancel={() => {
          setShowNavBlockerModal(false);
          setPendingNavPath(null);
        }}
        variant="destructive"
        icon={AlertCircle}
        zIndex="z-[10001]"
      />

      {/* Notification Soft Prompt Modal */}
      <NotificationConsentModal
        isOpen={showConsentModal}
        onOpenChange={setShowConsentModal}
        onConfirm={confirmConsent}
        onCancel={cancelConsent}
        portal="student"
      />

      {/* Call AI Tutor components (Modals & Overlay) */}
      {portal === 'student' && config?.voice_assistant_enabled && !isGuest && (
        <>

          {/* Confirmation Modal */}
          <CallConfirmationModal
            open={showCallConfirm}
            onClose={() => setShowCallConfirm(false)}
            subjectName={selectedSubject || (subjects && subjects[0]?.label) || 'Science'}
            subjectId={selectedSubjectId}
            chapterId={selectedChapterId}
            chapterName={selectedChapter}
            allowFallbackTutor
            autoStartWhenReady
            onConfirm={(tutor) => {
              const latestVoiceLessonContext = getVoiceLessonContext();
              if (latestVoiceLessonContext?.lessonManifest?.cards?.length || latestVoiceLessonContext?.lesson_manifest?.cards?.length) {
                setVoiceLessonContext(latestVoiceLessonContext);
              }
              setActiveTutor(tutor);
              setShowCallConfirm(false);
              setShowCallOverlay(true);
            }}
            onSeeSubscriptions={() => {
              setShowCallConfirm(false);
              setShowUpgradePlanModal(true);
            }}
          />

          {/* AI Consent Modal */}
          <AIConsentModal
            open={showAIConsent}
            onAccept={handleAcceptAIConsent}
            onDecline={handleDeclineAIConsent}
          />

          {/* Realtime Call Overlay */}
          <RealtimeVoiceAssistantOverlay
            open={showCallOverlay}
            onClose={(summary) => {
              setShowCallOverlay(false);
              setActiveTutor(null);
              if (summary) {
                setCallSummaryData(summary);
                setShowCallSummary(true);
              }
            }}
            tutor={activeTutor}
            context={{
              ...((voiceLessonContext?.lessonManifest?.cards?.length || voiceLessonContext?.lesson_manifest?.cards?.length) ? voiceLessonContext : {}),
              session_id: '',
              subject_id: selectedSubjectId || '',
              chapter_id: selectedChapterId || '',
              subject_name: selectedSubject || activeTutor?.subject_name || '',
              chapter_name: selectedChapter || '',
              subjectName: selectedSubject || activeTutor?.subject_name || '',
              chapterName: selectedChapter || '',
              tutor_id: activeTutor?.id || '',
              tutorId: activeTutor?.id || '',
              page_context: 'voice-call',
              pageContext: 'voice-call',
            }}
            onToolCall={async ({ name, arguments: args }) => {
              // RAG tool call handler for global voice assistant
              if (name === 'budlee_chat_answer') {
                const question = args?.question || '';
                try {
                  const { getOrCreateSession, sendChatMessage } = await import('@/api/chat');
                  const session = await getOrCreateSession({
                    page_context: 'voice-call',
                    subject_name: activeTutor?.subject_name || 'Science',
                  });
                  const result = await sendChatMessage(session.session_id, question, null, null, null, args.mode || 'textbook');
                  return {
                    answer: result?.answer || 'I am sorry, I could not find that information.',
                    mode: args.mode || 'textbook',
                  };
                } catch (err) {
                  console.error('Error handling tool call in voice assistant:', err);
                  return {
                    answer: 'I encountered an error while searching the textbook.',
                    mode: 'textbook',
                  };
                }
              }
              throw new Error(`Unknown voice tool: ${name}`);
            }}
          />

          {/* Call Summary Modal */}
          <CallSummaryModal
            open={showCallSummary}
            onClose={() => {
              setShowCallSummary(false);
              setCallSummaryData(null);
            }}
            summary={callSummaryData}
            onSeeSubscriptions={() => {
              setShowCallSummary(false);
              setCallSummaryData(null);
              setShowUpgradePlanModal(true);
            }}
          />
        </>
      )}

    </div>
  );
}
